import { useCallback, useEffect, useMemo } from 'react'
import { debounce } from '@/core'
import { enhancedApi as subsidiariesApi, TenantHierarchyFilter } from '../../../services/api/tenant/subsidiaries'
import { useTypedDispatch } from '../../../store'
import { useTenantSearchFormContext } from './useTenantSearchFormContext'

const useSearchTenantHierarchiesState = (
  filterBy: TenantHierarchyFilter,
  query?: string,
  page?: number,
  size?: number,
) => {
  const { data, isFetching, isUninitialized } = subsidiariesApi.endpoints.listTenantHierarchies.useQueryState({
    filterBy,
    query,
    page,
    size,
  })

  const normalized = useMemo(() => {
    if (!data) return undefined
    if (Array.isArray(data)) return { items: data, total: data.length }
    return { items: data.items || [], total: data.total || 0 }
  }, [data])

  return { data: normalized, isFetching, isUninitialized }
}

export const useSearchTenantHierarchies = (page?: number, size?: number) => {
  const dispatch = useTypedDispatch()
  const { values } = useTenantSearchFormContext()
  const query = (values?.name || '').trim() || undefined
  const filterBy: TenantHierarchyFilter = values?.type || 'all'

  const searchFn = useCallback(
    (args: { filterBy: TenantHierarchyFilter; query?: string; page?: number; size?: number }) => {
      dispatch(subsidiariesApi.endpoints.listTenantHierarchies.initiate(args))
    },
    [dispatch],
  )

  const debouncedSearchFn = useMemo(
    () => debounce((args: Parameters<typeof searchFn>[0]) => searchFn(args), 100),
    [searchFn],
  )

  useEffect(() => {
    debouncedSearchFn({ filterBy, query, page, size })
  }, [query, page, size, filterBy, debouncedSearchFn])

  const result = useSearchTenantHierarchiesState(filterBy, query, page, size)

  return result
}
